import React from 'react';

interface TabContentSkeletonProps {
  /**
   * Number of sections to display in the skeleton
   */
  sectionCount?: number;

  /**
   * Number of rows per section
   */
  rowsPerSection?: number;

  /**
   * Whether to display a card title
   */
  showTitle?: boolean;

  /**
   * Whether to show the sidebar
   */
  withSidebar?: boolean;

  /**
   * Additional class names
   */
  className?: string;
}

/**
 * TabContentSkeleton component
 *
 * This component provides a generic skeleton for tab content with customizable layouts.
 */
export const TabContentSkeleton: React.FC<TabContentSkeletonProps> = ({
  sectionCount = 2,
  rowsPerSection = 5,
  showTitle = true,
  withSidebar = false,
  className = '',
}) => {
  return (
    <div
      className={`flex flex-col-reverse lg:grid ${withSidebar ? 'lg:grid-cols-3' : 'lg:grid-cols-1'} gap-6 mb-16 ${className}`}
    >
      {/* Main content */}
      <div className={`${withSidebar ? 'lg:col-span-2' : 'w-full'}`}>
        <div className="bg-white rounded-xl p-6 border border-[#E4E8EA] animate-pulse">
          {/* Section title */}
          {showTitle && (
            <div className="h-6 w-48 bg-gray-200 rounded mb-6"></div>
          )}

          {/* Content sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {[...Array(sectionCount)].map((_, sectionIndex) => (
              <div key={sectionIndex} className="space-y-4">
                {/* Section title */}
                <div className="h-5 w-32 bg-gray-200 rounded mb-2"></div>
                {/* Rows */}
                {[...Array(rowsPerSection)].map((_, rowIndex) => (
                  <div key={rowIndex} className="flex">
                    <div className="h-5 w-32 bg-gray-200 rounded mr-4"></div>
                    <div className="h-5 w-48 bg-gray-100 rounded"></div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sidebar */}
      {withSidebar && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 border border-[#E4E8EA] animate-pulse">
            <div className="h-6 w-36 bg-gray-200 rounded mb-4"></div>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex flex-col space-y-2">
                  <div className="h-5 w-24 bg-gray-200 rounded"></div>
                  <div className="h-6 w-full bg-gray-100 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
